import axios from "axios";
import Cookies from "js-cookie";
import React, { ReactNode, useEffect, useState } from "react";

import { getCookieDomain, getInitialLanguage, getVersion } from "./helpers";
import {
  ContextState,
  TranslationContext,
  Translations,
} from "./TranslationContext";

export interface Props {
  document: string;
  clientId: string;
  children: ReactNode;
  fallback: Translations;
}

export const TranslationProvider: React.FC<Props> = ({
  clientId,
  document,
  children,
  fallback,
}: Props): React.ReactElement => {
  const version = getVersion();
  const [language, setLanguage] = useState(getInitialLanguage());
  const [fetchedLanguages, setFetchedLanguages] = useState<string[]>([]);
  const [translations, setTranslations] = useState<Translations>({});
  const [loading, setLoading] = useState(true);

  function getText(key: string, onMissingKey?: () => string): string {
    const fmsValue = getFMSValue<string>(key);
    if (fmsValue) {
      return fmsValue;
    }
    if (onMissingKey) {
      return onMissingKey();
    }
    return `??${key}??`;
  }

  function getJSON<T>(key: string): T {
    const fmsJsonValue = getFMSValue<T>(key);
    if (fmsJsonValue) {
      return fmsJsonValue;
    }
    return {} as T;
  }

  function getFMSValue<T>(key: string): T {
    const item = translations?.[language]?.[key];
    if (item) {
      return item as T;
    }
    return null;
  }

  async function fetchLanguage(lang: string) {
    if (!fetchedLanguages.includes(lang)) {
      const url = `https://api.storebrand.no/open/content/texts/${document}?version=${version}&lang=${lang}`;
      axios
        .get(url, { headers: { clientId } })
        .then(({ data }) => {
          setTranslations(() => ({
            ...translations,
            [lang]: data,
          }));
        })
        .catch(() => {
          const fallbackTranslation = fallback[lang];
          setTranslations({
            ...translations,
            [lang]: fallbackTranslation,
          });
        })
        .finally(() => {
          setLanguage(lang);
          setFetchedLanguages([...fetchedLanguages, lang]);
          setLoading(false);
        });
    } else {
      setLanguage(lang);
    }
  }

  function changeLanguage(newLanguage: string) {
    const newCustomerLanguage = newLanguage === "en" ? "ENGLISH" : "NORWEGIAN";
    const oldCustomerLanguage = language === "en" ? "ENGLISH" : "NORWEGIAN";

    if (newCustomerLanguage !== oldCustomerLanguage) {
      Cookies.set("lang", newLanguage, {
        path: "/",
        domain: getCookieDomain(),
      });
      axios.put("/customer/core-data", {
        language: newCustomerLanguage,
      });
    }

    fetchLanguage(newLanguage);
  }

  useEffect(() => {
    fetchLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <></>;
  }

  const providerValue: ContextState = {
    translations,
    language,
    changeLanguage,
    getText,
    getJSON,
    textsLoading: loading,
  };

  return (
    <TranslationContext.Provider value={providerValue}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
